import { ToastOptions, useIonToast } from '@ionic/react';
import { HookOverlayOptions } from '@ionic/react/dist/types/hooks/HookOverlayOptions';
import { useCallback } from 'react';
import { checkmarkCircle, close, alertCircle, warning } from 'ionicons/icons';
import './useToast.scss';

export type ToastType = 'success' | 'danger' | 'warning';

type ToastResult = [
  (options: ToastOptions & HookOverlayOptions, type: ToastType, hideClose?: boolean) => Promise<void>,
  () => Promise<void>,
];

/**
 * Get the properties the toast based on the type
 * @param type
 *
 * @returns The icon and css class for the toast
 */
const getToastProperties = (type: ToastType) => {
  switch (type) {
    case 'success':
      return { icon: checkmarkCircle, cssClass: 'success-toast' };
    case 'danger':
      return { icon: alertCircle, cssClass: 'danger-toast' };
    case 'warning':
      return { icon: warning, cssClass: 'warning-toast' };
    default:
      return { icon: undefined, cssClass: undefined };
  }
};

/**
 * Get the buttons for the toast.  Close Button is always present by default and custom buttons can be added.
 *
 * @param hideClose
 * @param customButtons
 * @param dismiss
 * @returns
 */
const getToastButtons = (hideClose: boolean, customButtons: any[] | undefined, dismiss: () => Promise<void>) => {
  const closeButton = [
    {
      side: 'end',
      icon: close,
      handler: dismiss,
    },
  ];

  if (hideClose && !customButtons) {
    return;
  }
  if (customButtons && hideClose) {
    return customButtons;
  }
  if (customButtons && !hideClose) {
    return [...closeButton, ...customButtons];
  }
  return closeButton;
};

/**
 * Custom hook to present a consistent toast throughout the app.
 * Default options are based on standardized designs of the Toast.
 *
 * @returns A tuple containing a function to present a custom toast and a function to dismiss it.
 */
export const useToast = (): ToastResult => {
  const [present, dismiss] = useIonToast();

  /**
   * Presents a custom toast with specified options and type.
   *
   * @param customOptions Custom options for the toast.
   * @param type Type of the toast.
   * @param hideClose boolean indicating whether to hide the close button (default: false).
   * @returns A promise resolving when the toast is presented.
   */
  const customPresent = useCallback(
    (customOptions: ToastOptions & HookOverlayOptions, type: ToastType, hideClose: boolean = false) => {
      // Default options for the toast
      const defaultOptions: ToastOptions & HookOverlayOptions = {
        color: 'light',
        icon: getToastProperties(type).icon,
        position: 'top',
        duration: 5000,
        cssClass: getToastProperties(type).cssClass,
        buttons: getToastButtons(hideClose, customOptions.buttons, dismiss),
      };

      // Combine the default options with the custom options; default options will be overridden by custom options
      const combinedOptions = { ...defaultOptions, ...customOptions };
      return present(combinedOptions);
    },
    [dismiss, present],
  );

  return [customPresent, dismiss];
};
